<script>
import { useAlert } from 'dashboard/composables';
import FlowPreview from './FlowPreview.vue';
import PopoverPortal from './PopoverPortal.vue';
import {
  FLOW_ACTION_TYPES,
  BLOCK_LABEL_OVERRIDE,
} from 'dashboard/constants/manualActionTypes';
import { AUTOMATION_ACTION_TYPES } from 'dashboard/constants/automationVocabulary';
import { CONVERSATION_EVENTS } from '../../../../helper/AnalyticsHelper/events';

// Fluxo manual (motor novo) na aba de macros da conversa. Mesmo visual e mesmos
// affordances do MacroItem; a execução vai para POST /automation_flows/:id/run.
//
// O preview cobre TODOS os passos do grafo (não só ações do vocabulário de
// macro): passo sem nome resolvível mostrava lista vazia e o botão sumia — era
// o bug do "Visualizar Macro" ausente. A travessia segue todas as arestas
// (inclusive as dos ramos de condição/menu/A-B), em largura, a partir do
// gatilho.
const KNOWN_ACTIONS = new Set(FLOW_ACTION_TYPES.map(a => a.key));
const ACTION_LABELS = {};
[...FLOW_ACTION_TYPES, ...AUTOMATION_ACTION_TYPES].forEach(a => {
  if (!ACTION_LABELS[a.key]) ACTION_LABELS[a.key] = a.label;
});

// Polling da execução: intervalo curto no começo (fluxos de conversa terminam
// em segundos) e teto de tentativas — waiting_input/waiting_timer encerram o
// acompanhamento com aviso, não com erro.
const POLL_INTERVAL_MS = 1200;
const POLL_MAX_ATTEMPTS = 50;

export default {
  components: {
    FlowPreview,
    PopoverPortal,
  },
  props: {
    flow: {
      type: Object,
      required: true,
    },
    conversationId: {
      type: [Number, String],
      required: true,
    },
  },
  data() {
    return {
      isExecuting: false,
      showPreview: false,
      showInputForm: false,
      inputValues: {},
      // balão de resultado: { kind: 'success'|'error'|'waiting', output, message }
      result: null,
      pollTimer: null,
      // aborta o polling se o componente for destruído com um fetch em voo
      isDestroyed: false,
      // Âncora dos popovers: eles vivem em document.body (PopoverPortal), então
      // precisam do elemento da linha para se posicionar.
      rowEl: null,
    };
  },
  computed: {
    // `left` + `viewport`: o painel e a ultima coluna da tela e o padrao do
    // projeto e `boundariesElement: 'window'`, que no Popper 1 e o documento
    // inteiro -- dica deitada para cima sairia do quadro pela direita.
    previewTooltipOptions() {
      return this.tooltipOptions(
        this.$t('AUTOMATION_FLOWS.MANUAL_RUN.PREVIEW')
      );
    },
    runTooltipOptions() {
      return this.tooltipOptions(
        this.$t('AUTOMATION_FLOWS.MANUAL_RUN.BUTTON_TOOLTIP')
      );
    },
    inputSchema() {
      return Array.isArray(this.flow.input_schema)
        ? this.flow.input_schema
        : [];
    },
    // FlowPreview espera { name, actions[] }; cada entrada leva display_name
    // já resolvido (o resolveActionName do helper estoura com vocabulário
    // desconhecido).
    previewFlow() {
      return { name: this.flow.name, actions: this.orderedSteps };
    },
    orderedSteps() {
      const def = this.flow.definition || {};
      const byId = {};
      (def.nodes || []).forEach(n => {
        byId[n.id] = n;
      });
      const edgeById = {};
      (def.edges || []).forEach(e => {
        edgeById[e.id] = e;
      });
      const follow = edgeId => {
        const edge = edgeById[edgeId];
        return edge && edge.to ? edge.to.node_id : null;
      };

      // Largura a partir do gatilho, seguindo TODAS as saídas: a do nó, as dos
      // ramos do nó (formato antigo) e as dos ramos de cada passo.
      const steps = [];
      const visited = new Set();
      const trigger = (def.nodes || []).find(n => n.type === 'trigger');
      const queue = trigger ? [trigger.id] : [];
      while (queue.length) {
        const node = byId[queue.shift()];
        if (!node || visited.has(node.id)) continue; // eslint-disable-line no-continue
        visited.add(node.id);
        (node.steps || []).forEach(s => {
          steps.push(s);
          (s.branches || []).forEach(b => {
            const to = follow(b.outgoing_edge_id);
            if (to) queue.push(to);
          });
        });
        (node.branches || []).forEach(b => {
          const to = follow(b.outgoing_edge_id);
          if (to) queue.push(to);
        });
        const to = follow(node.outgoing_edge_id);
        if (to) queue.push(to);
      }

      return steps.map(s => this.previewEntry(s)).filter(Boolean);
    },
    hasPreview() {
      return this.orderedSteps.length > 0;
    },
    resultTitle() {
      if (!this.result) return '';
      if (this.result.kind === 'success')
        return this.$t('AUTOMATION_FLOWS.MANUAL_RUN.RESULT.SUCCESS');
      if (this.result.kind === 'error')
        return this.$t('AUTOMATION_FLOWS.MANUAL_RUN.RESULT.FAILURE');
      return this.$t('AUTOMATION_FLOWS.MANUAL_RUN.RESULT.WAITING');
    },
    resultIcon() {
      if (!this.result) return 'clock-outline';
      if (this.result.kind === 'success') return 'checkmark-circle-outline';
      if (this.result.kind === 'error') return 'dismiss-circle-outline';
      return 'clock-outline';
    },
    resultOutput() {
      return this.result && this.result.output ? this.result.output : {};
    },
    hasResultOutput() {
      return Object.keys(this.resultOutput).length > 0;
    },
  },
  mounted() {
    // A linha é a âncora dos popovers portados para document.body.
    this.rowEl = this.$el;
  },
  beforeDestroy() {
    this.isDestroyed = true;
    clearTimeout(this.pollTimer);
  },
  methods: {
    tooltipOptions(content) {
      return {
        content,
        placement: 'left',
        boundariesElement: 'viewport',
        container: 'body',
      };
    },
    stepLabel(step) {
      const key = step.action_name || step.type;
      // Aqui o que se mostra é PASSO DE FLUXO, então o catálogo de ações perde
      // para o de blocos nas chaves do BLOCK_LABEL_OVERRIDE — senão o preview
      // na conversa chama o passo por um nome que o editor não usa (#934).
      if (ACTION_LABELS[key] && !BLOCK_LABEL_OVERRIDE.has(key))
        return ACTION_LABELS[key];
      const path = `AUTOMATION_FLOWS.BLOCKS.LABELS.${key}`;
      return this.$te(path) ? this.$t(path) : key;
    },
    previewEntry(step) {
      const key = step.action_name || step.type;
      if (!key) return null;
      const params =
        step.type === 'action' && KNOWN_ACTIONS.has(key)
          ? this.previewParams((step.config || {}).action_params)
          : [];
      return {
        action_name: key,
        action_params: params,
        display_name: this.stepLabel(step),
      };
    },
    // O preview é um RESUMO: params em formato de objeto (ex.: send_message
    // {message, inbox_id}) mostravam JSON cru. Extrai só o texto amigável;
    // objeto sem texto não mostra valor nenhum.
    previewParams(params) {
      const list = Array.isArray(params) ? params : [params];
      const first = list[0];
      if (first && typeof first === 'object' && !Array.isArray(first)) {
        return typeof first.message === 'string' && first.message
          ? [first.message]
          : [];
      }
      return list.filter(p => p !== null && p !== undefined);
    },
    onRunClick() {
      if (this.isExecuting) return;
      // Fluxo com formulário de entrada: pergunta antes de disparar.
      if (this.inputSchema.length) {
        this.inputValues = this.inputSchema.reduce(
          (acc, field) => ({ ...acc, [field.name]: '' }),
          {}
        );
        this.showInputForm = true;
        return;
      }
      this.runFlow();
    },
    async runFlow(variables = {}) {
      // Guarda de reentrância: Enter no formulário e clique no botão Executar
      // chamam runFlow direto (sem passar pelo onRunClick), então sem isto um
      // Enter + clique, ou duplo-clique, dispararia duas sessões na conversa.
      if (this.isExecuting) return;
      this.showInputForm = false;
      this.result = null;
      try {
        this.isExecuting = true;
        const data = await this.$store.dispatch('automationFlows/run', {
          id: this.flow.id,
          conversationIds: [this.conversationId],
          variables,
        });
        this.$track(CONVERSATION_EVENTS.EXECUTED_A_MACRO);
        const sessionId = data && data.session_ids && data.session_ids[0];
        if (sessionId) {
          this.pollSession(sessionId, 0);
        } else {
          this.isExecuting = false;
          useAlert(
            this.$t('AUTOMATION_FLOWS.MANUAL_RUN.EXECUTED_SUCCESSFULLY')
          );
        }
      } catch (error) {
        this.isExecuting = false;
        useAlert(this.$t('AUTOMATION_FLOWS.MANUAL_RUN.ERROR'));
      }
    },
    async pollSession(sessionId, attempt) {
      // O fetch é assíncrono: se a conversa fechar no meio, beforeDestroy já
      // rodou mas a promessa em voo resolveria depois e reagendaria o timer,
      // seguindo o polling num componente morto. Aborta após cada await.
      if (this.isDestroyed) return;
      try {
        const session = await this.$store.dispatch(
          'automationFlows/fetchSession',
          { id: this.flow.id, sessionId }
        );
        if (this.isDestroyed) return;
        const status = session && session.status;
        if (status === 'completed') {
          this.finishWith({ kind: 'success', output: session.output });
          return;
        }
        if (status === 'failed' || status === 'canceled') {
          this.finishWith({
            kind: 'error',
            message:
              session.error_message ||
              this.$t('AUTOMATION_FLOWS.MANUAL_RUN.RESULT.FAILURE_GENERIC'),
          });
          return;
        }
        if (status === 'waiting_input' || status === 'waiting_timer') {
          // Pausou de propósito (pergunta ao contato / espera) — o resultado
          // não vem neste fôlego; avisa e libera.
          this.finishWith({ kind: 'waiting' });
          return;
        }
      } catch (error) {
        // leitura falhou (rede); tenta de novo dentro do teto
      }
      if (attempt >= POLL_MAX_ATTEMPTS) {
        this.finishWith({ kind: 'waiting' });
        return;
      }
      this.pollTimer = setTimeout(
        () => this.pollSession(sessionId, attempt + 1),
        POLL_INTERVAL_MS
      );
    },
    finishWith(result) {
      this.isExecuting = false;
      this.result = result;
    },
    dismissResult() {
      this.result = null;
    },
    togglePreview() {
      this.showPreview = !this.showPreview;
    },
    closePreview() {
      this.showPreview = false;
    },
    closeInputForm() {
      this.showInputForm = false;
    },
  },
};
</script>

<template>
  <!-- Cartao, e nao botao: o `button secondary clear` global trazia junto a
       altura, o padding e o `text-sm` de um botao do app, e por isso o nome do
       fluxo saia maior que o texto das outras secoes do painel. A forma vem da
       referencia da #1243 -- contorno cheio, fundo rebaixado, 12.5px. -->
  <div class="flow" :class="{ executing: isExecuting }">
    <span class="overflow-hidden text-xs whitespace-nowrap text-ellipsis">{{
      flow.name
    }}</span>
    <div class="flex items-center gap-1 flows-actions">
      <!-- Executando: setas gêmeas girando no lugar dos botões -->
      <span
        v-if="isExecuting"
        class="flow-running"
        :title="$t('AUTOMATION_FLOWS.MANUAL_RUN.RUNNING')"
      >
        <fluent-icon icon="refresh-outline" size="14" class="spin" />
      </span>
      <!-- Quadrados de 26px com contorno, como na referencia da #1243: o de
           ver fica na cor do texto e o de executar em azul, porque sao a acao
           secundaria e a principal do cartao. O `woot-button` que estava aqui
           trazia altura e padding de botao do app, grandes demais dentro de um
           cartao de 36px. -->
      <template v-else>
        <button
          v-if="hasPreview"
          v-tooltip="previewTooltipOptions"
          type="button"
          :aria-label="$t('AUTOMATION_FLOWS.MANUAL_RUN.PREVIEW')"
          class="flow-action"
          @click="togglePreview"
        >
          <fluent-icon icon="eye-show" size="16" />
        </button>
        <button
          v-tooltip="runTooltipOptions"
          type="button"
          :aria-label="$t('AUTOMATION_FLOWS.MANUAL_RUN.BUTTON_TOOLTIP')"
          class="flow-action flow-action--run"
          @click="onRunClick"
        >
          <fluent-icon icon="play-circle" size="16" />
        </button>
      </template>
    </div>
    <!-- Preview, formulário e balão vivem em document.body (PopoverPortal): na
         árvore do sidebar há três ancestrais com overflow que os cortavam, e um
         `container-type: inline-size` que prende até `position: fixed`. As
         <transition> saíram junto: `menu-slide` nunca teve CSS neste projeto e
         `result-blow` virou animação dentro do PopoverPortal (transição do Vue
         não sobrevive ao re-parenteamento do elemento). -->
    <PopoverPortal
      v-if="showPreview"
      v-on-clickaway="closePreview"
      :anchor="rowEl"
    >
      <FlowPreview :flow="previewFlow" />
    </PopoverPortal>
    <!-- Formulário das variáveis de entrada (labels vêm do bloco do fluxo) -->
    <PopoverPortal
      v-if="showInputForm"
      v-on-clickaway="closeInputForm"
      :anchor="rowEl"
    >
      <div class="flow-popover">
        <h6 class="text-sm text-body mb-2">{{ flow.name }}</h6>
        <div
          v-for="field in inputSchema"
          :key="field.name"
          class="flow-form-field"
        >
          <label>{{ field.label }}</label>
          <!-- A descrição vem do registro de variáveis da versão publicada
               (issue #568). Só aparece quando existe: campo sem descrição não
               deve ganhar uma linha vazia. O rótulo diz o NOME do campo; a
               descrição diz o que preencher, que é o que faltava para quem
               dispara o fluxo sem ter sido quem o montou. -->
          <p v-if="field.description" class="flow-form-hint">
            {{ field.description }}
          </p>
          <input
            v-model="inputValues[field.name]"
            type="text"
            class="!mb-0"
            :placeholder="field.description || field.label"
            @keyup.enter="runFlow(inputValues)"
          />
        </div>
        <div class="flex justify-end gap-2 mt-2">
          <woot-button
            size="tiny"
            variant="clear"
            color-scheme="secondary"
            @click="closeInputForm"
          >
            {{ $t('AUTOMATION_FLOWS.MANUAL_RUN.FORM.CANCEL') }}
          </woot-button>
          <woot-button size="tiny" @click="runFlow(inputValues)">
            {{ $t('AUTOMATION_FLOWS.MANUAL_RUN.FORM.RUN') }}
          </woot-button>
        </div>
      </div>
    </PopoverPortal>
    <!-- Balão de resultado, soprado para o lado ao terminar -->
    <PopoverPortal v-if="result" v-on-clickaway="dismissResult" :anchor="rowEl">
      <div class="flow-popover flow-result" :class="result.kind">
        <div class="flex items-center gap-1.5">
          <fluent-icon :icon="resultIcon" size="16" />
          <span class="flow-result__title">{{ resultTitle }}</span>
          <button class="flow-result__close" @click="dismissResult">
            <fluent-icon icon="dismiss-outline" size="12" />
          </button>
        </div>
        <p v-if="result.message" class="flow-result__msg">
          {{ result.message }}
        </p>
        <dl v-if="hasResultOutput" class="flow-result__vars">
          <template v-for="(value, name) in resultOutput">
            <dt :key="`k-${name}`">{{ name }}</dt>
            <dd :key="`v-${name}`">{{ value }}</dd>
          </template>
        </dl>
      </div>
    </PopoverPortal>
  </div>
</template>

<style scoped lang="scss">
.flow {
  @apply relative flex items-center justify-between gap-2 px-2.5 py-2 leading-4
    border rounded-lg bg-surface-sunken dark:bg-surface-raised border-line-faint
    text-body transition-colors duration-150;

  &:hover {
    @apply border-line;
  }

  .flows-actions {
    @apply flex items-center justify-end gap-1;
  }
}

// `p-0` nao e enfeite: o estilo global de `button` traz `padding: 4px 8px`, e
// com `box-sizing: border-box` numa caixa de 26px sobravam 8px de largura para
// o icone -- ele chegava achatado, 8x16, em vez de 16x16.
.flow-action {
  @apply flex items-center justify-center w-[1.625rem] h-[1.625rem] shrink-0 p-0
    border rounded-md bg-surface dark:bg-surface-sunken border-line text-body
    transition-colors duration-150;

  &:hover {
    @apply border-brand text-brand-text;
  }
}

.flow-action--run {
  @apply text-brand-text;
}
/* Executando: fundo esverdeado + setas girando. Tokens de rampa (invertem no
   escuro sozinhos). */
.flow.executing {
  background: rgb(var(--color-teal-100, 224 248 243));
}
.flow-running {
  display: grid;
  place-items: center;
  color: rgb(var(--color-teal-800, 0 133 115));
  padding: 2px 4px;
}
.spin {
  animation: flow-spin 0.9s linear infinite;
}
@keyframes flow-spin {
  to {
    transform: rotate(360deg);
  }
}
.flow-popover {
  /* Só a moldura: quem posiciona é o PopoverPortal (fixed, em document.body).
     O max-h fica porque o balão pode passar da altura da janela — agora ele
     rola por dentro, em vez de ser decapitado pelo scrollport do sidebar. */
  @apply w-64 max-h-[22.5rem] rounded-md bg-surface-raised overflow-y-auto p-3 text-left rtl:text-right;
  box-shadow: 0 8px 24px rgb(0 0 0 / 0.18);
  border: 1px solid rgb(var(--fd-line, 215 219 223));
}
.flow-form-field {
  margin-bottom: 8px;
  /* Dica abaixo do rótulo, menor e mais apagada: ela explica o campo, não
     compete com ele. */
  .flow-form-hint {
    font-size: 11.5px;
    line-height: 1.35;
    color: rgb(var(--fd-subtle, 110 117 123));
    margin: 0 0 4px;
  }
  label {
    display: block;
    font-size: 12px;
    font-weight: 600;
    margin-bottom: 3px;
    color: rgb(var(--fd-muted, 76 81 85));
  }
}
.flow-result {
  &.success {
    border-color: rgb(var(--color-teal-800, 0 133 115) / 0.5);
  }
  &.error {
    border-color: rgb(var(--color-ruby-800, 202 36 77) / 0.5);
  }
  &.success .flow-result__title,
  &.success svg {
    color: rgb(var(--color-teal-800, 0 133 115));
  }
  &.error .flow-result__title,
  &.error svg {
    color: rgb(var(--color-ruby-800, 202 36 77));
  }
}
.flow-result__title {
  font-size: 12.5px;
  font-weight: 650;
  flex: 1;
}
.flow-result__close {
  border: 0;
  background: transparent;
  cursor: pointer;
  color: rgb(var(--fd-subtle, 110 117 123));
  display: grid;
  place-items: center;
}
.flow-result__msg {
  font-size: 12px;
  margin: 6px 0 0;
  color: rgb(var(--fd-muted, 76 81 85));
}
.flow-result__vars {
  margin: 8px 0 0;
  font-size: 12px;
  dt {
    font-weight: 650;
    color: rgb(var(--fd-subtle, 110 117 123));
  }
  dd {
    margin: 0 0 4px;
    word-break: break-word;
  }
}
/* O "sopro" do balão virou @keyframes no PopoverPortal: com o elemento
   re-parenteado para document.body no mount, a <transition> do Vue não chega a
   rodar. */
</style>
